import React, { useState, useEffect, useCallback } from "react";
import { fetchWithAuth } from "../../utils/api";
import { firstName, writerFirstNames } from "../../utils/rowWriters";
import usePoll from "../../hooks/usePoll";
import { useAuth } from "../../context/AuthContext";
import "./Dashboard.css";

const TABS = [
  {
    id: "dashboard",
    label: "Dashboard",
    subtitle: "Live sales feed, collections, and leaderboard",
    roles: ["admin", "sales_manager", "service_writer", "sales_support"],
  },
  {
    id: "sales",
    label: "Sales",
    subtitle: "In-depth sales statistics and breakdowns",
    roles: ["admin", "sales_manager", "service_writer", "sales_support"],
  },
  {
    id: "collections",
    label: "Collections",
    subtitle: "Orders ready to be collected",
    roles: ["admin", "sales_manager", "service_writer", "sales_support"],
  },
  {
    id: "risk",
    label: "Risk",
    subtitle: "Customers with outstanding balances",
    roles: ["admin", "sales_manager"],
  },
  {
    id: "feedback",
    label: "Customer Feedback",
    subtitle: "Invoiced and fully paid orders ready for survey dispatch",
    roles: ["admin", "sales_manager"],
  },
  {
    id: "goals",
    label: "Operations Goals",
    subtitle: "Daily and weekly invoiced totals vs targets",
    roles: ["admin", "sales_manager", "technician_manager"],
  },
  {
    id: "osr",
    label: "OSR",
    subtitle: "Online Service Requests — live lead capture",
    roles: ["admin", "sales_manager", "service_writer", "sales_support"],
  },
];

function shortLocationName(name) {
  if (!name) return "—";
  const trimmed = name.trim();
  const dashIdx = trimmed.lastIndexOf("-");
  if (dashIdx !== -1) return trimmed.slice(dashIdx + 1).trim();
  return trimmed;
}

function shortName(fullName) {
  if (!fullName) return "—";
  const parts = fullName.trim().split(/\s+/);
  if (parts.length < 2) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
}

function formatCurrency(value) {
  const num = parseFloat(value) || 0;
  return num.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatDateTime(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

const LB_RANGES = [
  { value: "today", label: "Today" },
  { value: "week", label: "This Week" },
  { value: "month", label: "This Month" },
];

// ─── Error Card ──────────────────────────────────────────────────────────────
function ErrorCard({ message }) {
  return (
    <div className="card sm-error-card">
      <p>⚠ {message || "Failed to load data."}</p>
      <p className="sm-helper-text">
        Check the server connection and try again.
      </p>
    </div>
  );
}

// ─── Coming Soon Placeholder ─────────────────────────────────────────────────
function ComingSoonTab({ label }) {
  return (
    <div className="sm-tab-content">
      <div
        className="card"
        style={{ padding: 40, textAlign: "center" }}
      >
        <h3>{label}</h3>
        <p style={{ color: "var(--text-secondary)", marginTop: 8 }}>
          Coming Soon
        </p>
      </div>
    </div>
  );
}

// ─── Dashboard Tab ───────────────────────────────────────────────────────────
function DashboardTab({ data, loading, error, dashDate }) {
  const [lbRange, setLbRange] = useState("today");
  const [lbRows, setLbRows] = useState(null);
  const [lbLoading, setLbLoading] = useState(false);
  const [nsHover, setNsHover] = useState(false);

  const fetchLeaderboard = useCallback(
    async (range) => {
      if (range === "today") {
        setLbRows(null);
        return;
      }
      setLbLoading(true);
      try {
        const dateQs = dashDate ? `&date=${dashDate}` : "";
        const res = await fetchWithAuth(
          `/admin/dashboard/leaderboard?range=${range}${dateQs}`,
        );
        const json = await res.json();
        if (res.ok && json.success) setLbRows(json.leaderboard);
      } catch {
        /* silent */
      } finally {
        setLbLoading(false);
      }
    },
    [dashDate],
  );

  useEffect(() => {
    fetchLeaderboard(lbRange);
  }, [lbRange, dashDate, fetchLeaderboard]);

  usePoll(() => fetchLeaderboard(lbRange), 120000, { enabled: lbRange !== "today", immediate: false });

  if (loading && !data)
    return (
      <div className="sm-tab-content">
        <div className="sm-skeleton-loader">
          <div className="sm-skeleton-spinner" />
          <div className="sm-skeleton-text">Loading dashboard&hellip;</div>
          <div className="sm-skeleton-sub">
            Pulling sales, collections &amp; leaderboard data
          </div>
        </div>
      </div>
    );
  if (error) return <ErrorCard message={error} />;
  if (!data) return null;

  const {
    summary = {},
    yesterday = {},
    recentSales = [],
    recentCollections = [],
    leaderboard = [],
  } = data;
  const displayLeaderboard = lbRange === "today" ? leaderboard : (lbRows ?? []);

  const currentEST = new Date().toLocaleDateString("en-CA", {
    timeZone: "America/New_York",
  });
  const isToday = !dashDate || dashDate === currentEST;
  const dateLabel = isToday
    ? "Today"
    : new Date(dashDate + "T12:00:00").toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
  const prevLabel = isToday ? "Yesterday" : "Prior day";

  return (
    <>
      <div className="sm-dash-content">
        {/* Left column (2/3) */}
        <div className="sm-dash-left">
          {/* Row 1 — Stat cards */}
          <div className="sm-dash-top-row">
            <div className="sm-stat-card highlight">
              <div className="sm-stat-label">Sales {dateLabel}</div>
              <div className="sm-stat-value">{summary.total_sales || 0}</div>
              <div className="sm-stat-sub">
                Orders created
                {(parseInt(summary.estimate_count) > 0 ||
                  parseInt(summary.ns_count) > 0 ||
                  parseInt(summary.return_count) > 0) && (
                  <span className="sm-stat-tags">
                    {parseInt(summary.estimate_count) > 0 && (
                      <span
                        className="sm-stat-tag sm-stat-tag--est"
                        title="Estimate (open, not authorized)"
                      >
                        Est. {summary.estimate_count}
                      </span>
                    )}
                    {parseInt(summary.ns_count) > 0 && (
                      <span
                        className="sm-ns-tag-wrap"
                        onMouseEnter={() => setNsHover(true)}
                        onMouseLeave={() => setNsHover(false)}
                      >
                        <span className="sm-stat-tag sm-stat-tag--ns">
                          NS {summary.ns_count}
                        </span>
                        {nsHover && (
                          <div className="sm-ns-tooltip">
                            <div className="sm-ns-tooltip-title">
                              No Schedule — {summary.ns_count} total
                            </div>
                            {(summary.ns_breakdown || []).map((row) => (
                              <div key={row.name} className="sm-ns-tooltip-row">
                                <span className="sm-ns-tooltip-name">
                                  {row.name}
                                </span>
                                <span className="sm-ns-tooltip-count">
                                  {row.count}
                                </span>
                              </div>
                            ))}
                            <div className="sm-ns-tooltip-note">
                              NS orders are not counted toward the sold total.
                            </div>
                          </div>
                        )}
                      </span>
                    )}
                    {parseInt(summary.return_count) > 0 && (
                      <span
                        className="sm-stat-tag sm-stat-tag--ret"
                        title="Return Sales"
                      >
                        Ret. {summary.return_count}
                      </span>
                    )}
                  </span>
                )}
              </div>
              <div className="sm-stat-yesterday">
                {prevLabel}: {yesterday.jobs_sold || 0}
              </div>
            </div>
            <div className="sm-stat-card highlight">
              <div className="sm-stat-label">Collected {dateLabel}</div>
              <div className="sm-stat-value">
                {formatCurrency(summary.total_collected)}
              </div>
              <div className="sm-stat-sub">
                {summary.collections_count || 0} payments received
              </div>
              <div className="sm-stat-yesterday">
                {prevLabel}: {formatCurrency(yesterday.money_collected)}
              </div>
            </div>
          </div>

          {/* Row 2 — Leaderboard + Top Performers */}
          <div className="sm-dash-mid-row">
            <div className="card">
              <div className="card-header sm-lb-header">
                <h3>Leaderboard</h3>
                <div className="sm-lb-tabs">
                  {LB_RANGES.map((r) => (
                    <button
                      key={r.value}
                      className={`sm-lb-tab${lbRange === r.value ? " active" : ""}`}
                      onClick={() => setLbRange(r.value)}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>
              {lbLoading ? (
                <p className="empty-state">Loading…</p>
              ) : displayLeaderboard.length === 0 ? (
                <p className="empty-state">No sales yet.</p>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Service Writer</th>
                      <th>Sales</th>
                      <th>Collected</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayLeaderboard.map((row, i) => (
                      <tr key={i} className={i === 0 ? "sm-leader-row" : ""}>
                        <td>{i + 1}</td>
                        <td>{row.salesperson || "—"}</td>
                        <td>{row.job_count}</td>
                        <td>{formatCurrency(row.collected_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <TopPerformersCard dashDate={dashDate} />
          </div>

          {/* Row 3 — Weekly Goals */}
          <WeeklyGoalsSection dashDate={dashDate} />

          {/* Row 4 — Weekly Sales by Location */}
          <WeeklySalesByLocationCard dashDate={dashDate} />
        </div>

        {/* Right column (1/3) — Live Feeds */}
        <div className="sm-dash-feeds">
          <div className="card">
            <div className="card-header">
              <h3>Recent Sales</h3>
              <span className="sm-badge">
                {recentSales.length} · {isToday ? "last 24 hrs" : dateLabel}
              </span>
            </div>
            {recentSales.length === 0 ? (
              <p className="empty-state">
                No sales {isToday ? "in the last 24 hours" : `on ${dateLabel}`}.
              </p>
            ) : (
              <div className="sm-feed-cards">
                {recentSales.map((row, i) => {
                  const isEmergency = row.is_emergency === true;
                  const isNew = parseInt(row.previous_orders) === 0;
                  const isNS = row.is_ns === true;
                  const isEstimate = row.is_estimate === true;
                  return (
                    <div
                      key={i}
                      className={`sm-feed-card${isEmergency ? " sm-feed-card--emergency" : ""}${isNS ? " sm-feed-card--ns" : ""}${isEstimate ? " sm-feed-card--estimate" : ""}`}
                      title={
                        isNS
                          ? "No Schedule"
                          : isEstimate
                            ? "Estimate"
                            : undefined
                      }
                    >
                      <div className="sm-feed-card-icon">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20.26 24.34"
                          fill="currentColor"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M19.1,6.03l-1.53-.48c-.71-.22-1.45.06-1.87.68-.28.43-.82.61-1.3.46-.49-.15-.82-.61-.81-1.12.01-.75-.45-1.4-1.15-1.62l-.22-.07-.66-2.64c-.1-.42-.37-.78-.74-1.01-.38-.23-.82-.29-1.24-.19l-1.55.38c-.71.18-1.21.8-1.25,1.55-.02.51-.38.95-.88,1.07-.5.12-1.02-.1-1.28-.54-.38-.65-1.11-.97-1.82-.79l-1.55.38C.37,2.32-.17,3.22.05,4.1l4.04,16.1c0,.26.07.5.18.72.2.39.55.67.96.8l8.19,2.55c.16.05.32.07.49.07.72,0,1.35-.46,1.57-1.15l4.69-15.08v-.03c.28-.86-.21-1.78-1.07-2.05ZM9.66,3.59c-.39.2-.67.55-.8.97l-3.81,12.24L1.79,3.79l1.36-.31c.66,1.06,1.96,1.58,3.17,1.29,1.22-.3,2.12-1.38,2.21-2.64l1.35-.33.41,1.63c-.22.02-.43.08-.63.18ZM18.46,7.68l-4.63,14.87-7.96-2.48,4.63-14.87,1.32.44c.02,1.25.85,2.37,2.05,2.74,1.2.38,2.53-.09,3.26-1.12l1.32.42Z" />
                        </svg>
                      </div>
                      <div className="sm-feed-card-body">
                        <div className="sm-feed-card-title">
                          {writerFirstNames(row)} ({row.order_number}){" "}
                          <span
                            className={
                              row.membership
                                ? `sm-member-${row.membership}`
                                : ""
                            }
                          >
                            {row.customer_name || ""}
                          </span>
                          {isEmergency && (
                            <span className="sm-feed-emergency">
                              {" "}
                              - EMERGENCY
                            </span>
                          )}
                          {isNS && <span className="sm-feed-ns-tag">NS</span>}
                          {isEstimate && (
                            <span className="sm-feed-est-tag">Est</span>
                          )}
                        </div>
                        <div className="sm-feed-card-sub">
                          {isNew ? "New Sale" : "Return Sale"} •{" "}
                          {formatDateTime(row.created_date)} • ⚲{" "}
                          {shortLocationName(row.location_name)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="card">
            <div className="card-header">
              <h3>Recent Collections</h3>
              <span className="sm-badge">
                {recentCollections.length} ·{" "}
                {isToday ? "last 36 hrs" : dateLabel}
              </span>
            </div>
            {recentCollections.length === 0 ? (
              <p className="empty-state">
                No collections{" "}
                {isToday ? "in the last 36 hours" : `on ${dateLabel}`}.
              </p>
            ) : (
              <div className="sm-feed-cards">
                {recentCollections.map((row, i) => (
                  <div
                    key={i}
                    className={`sm-feed-card${parseFloat(row.amount) >= 2000 ? " sm-feed-card--big-collection" : ""}`}
                  >
                    <div className="sm-feed-card-icon sm-feed-icon-collection">
                      $
                    </div>
                    <div className="sm-feed-card-body">
                      <div className="sm-feed-card-title">
                        {writerFirstNames(row)} ({row.order_number})
                      </div>
                      <div className="sm-feed-card-sub">
                        Collection • {formatDateTime(row.payment_date)}
                      </div>
                    </div>
                    <div className="sm-feed-card-amount">
                      {formatCurrency(row.amount)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Top Performers Card ─────────────────────────────────────────────────────
function TopPerformersCard({ dashDate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setError(null);
    try {
      const qs = dashDate ? `?date=${dashDate}` : "";
      const res = await fetchWithAuth(`/admin/dashboard/top-performers${qs}`);
      const json = await res.json();
      if (res.ok && json.success) setData(json);
      else setError(json.error || "Failed to load top performers.");
    } catch {
      setError("Could not connect.");
    } finally {
      setLoading(false);
    }
  }, [dashDate]);

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [fetchData]);
  usePoll(fetchData, 120000, { immediate: false });

  if (loading)
    return <div className="tab-loading">Loading top performers…</div>;
  if (error) return <ErrorCard message={error} />;
  if (!data) return null;

  const {
    today: todayData,
    yesterday: yesterdayData,
    thisWeek,
    lastWeek,
  } = data;

  const currentEST = new Date().toLocaleDateString("en-CA", {
    timeZone: "America/New_York",
  });
  const isCurrentDay = !dashDate || dashDate === currentEST;
  const dayLabel = isCurrentDay
    ? "Today"
    : new Date(dashDate + "T12:00:00").toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
  const prevDayLabel = isCurrentDay ? "Yesterday" : "Prior day";

  return (
    <div className="card sm-tp-card">
      <div className="sm-tp-section">
        <div className="sm-tp-label">Top Performer ({dayLabel})</div>
        {todayData ? (
          <>
            <div className="sm-tp-name">{todayData.first_name}</div>
            <div className="sm-tp-stats">
              {todayData.job_count} jobs&nbsp;/&nbsp;
              {formatCurrency(todayData.collected_amount)} collected
            </div>
            <div className="sm-tp-compare">
              {yesterdayData ? (
                <>
                  {prevDayLabel}: {yesterdayData.first_name} —{" "}
                  {yesterdayData.job_count} jobs /{" "}
                  {formatCurrency(yesterdayData.collected_amount)}
                </>
              ) : (
                <>{prevDayLabel}: no sales</>
              )}
            </div>
          </>
        ) : (
          <div className="sm-tp-empty">
            No sales {isCurrentDay ? "yet today" : `on ${dayLabel}`}
          </div>
        )}
      </div>

      <div className="sm-tp-divider" />

      <div className="sm-tp-section">
        <div className="sm-tp-label">MVP of the Week</div>
        {thisWeek ? (
          <>
            <div className="sm-tp-name sm-tp-name-week">
              {firstName(thisWeek.full_name)}
            </div>
            <div className="sm-tp-stats">
              {thisWeek.job_count} jobs&nbsp;/&nbsp;
              {formatCurrency(thisWeek.collected_amount)} collected
            </div>
            <div className="sm-tp-compare">
              {lastWeek ? (
                <>
                  Last week: {lastWeek.full_name} — {lastWeek.job_count} jobs /{" "}
                  {formatCurrency(lastWeek.collected_amount)}
                </>
              ) : (
                <>Last week: no data</>
              )}
            </div>
          </>
        ) : (
          <div className="sm-tp-empty">No sales yet this week</div>
        )}
      </div>
    </div>
  );
}

// ─── Weekly Sales by Location Card ───────────────────────────────────────────
function WeeklySalesByLocationCard({ dashDate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeId, setActiveId] = useState(null);

  const fetchData = useCallback(async () => {
    setError(null);
    try {
      const qs = dashDate ? `?date=${dashDate}` : "";
      const res = await fetchWithAuth(
        `/admin/dashboard/weekly-sales-by-location${qs}`,
      );
      const json = await res.json();
      if (res.ok && json.success) {
        setData(json);
        setActiveId((prev) => prev ?? json.locations[0]?.location_id ?? null);
      } else {
        setError(json.error || "Failed to load weekly sales.");
      }
    } catch {
      setError("Could not connect.");
    } finally {
      setLoading(false);
    }
  }, [dashDate]);

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [fetchData]);
  usePoll(fetchData, 120000, { immediate: false });

  if (loading)
    return <div className="tab-loading">Loading weekly sales by location…</div>;
  if (error) return <ErrorCard message={error} />;
  if (!data) return null;

  const { locations, orders, weekStart } = data;

  const ordersByLocation = {};
  for (const o of orders) {
    if (!ordersByLocation[o.location_id]) ordersByLocation[o.location_id] = [];
    ordersByLocation[o.location_id].push(o);
  }

  const activeOrders = ordersByLocation[activeId] || [];

  return (
    <div className="card">
      <div className="card-header">
        <h3>Weekly Sales by Location</h3>
        <span className="sm-badge">Week of {formatDate(weekStart)}</span>
      </div>

      <div className="sm-loc-tabs">
        {locations.map((loc) => (
          <button
            key={loc.location_id}
            className={`sm-loc-tab${activeId === loc.location_id ? " active" : ""}`}
            onClick={() => setActiveId(loc.location_id)}
          >
            {shortLocationName(loc.location_name)}
            <span className="sm-loc-tab-count">
              {ordersByLocation[loc.location_id]?.length || 0}
            </span>
          </button>
        ))}
      </div>

      {activeOrders.length === 0 ? (
        <p className="empty-state" style={{ padding: "20px" }}>
          No sales this week for this location.
        </p>
      ) : (
        <div className="sm-feed-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Order #</th>
                <th>Customer</th>
                <th>Service Writer</th>
                <th>Status</th>
                <th>Amount</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {activeOrders.map((row, i) => (
                <tr key={i}>
                  <td>{row.order_number || "—"}</td>
                  <td>{row.customer_name || "—"}</td>
                  <td>{row.sales_writer || "—"}</td>
                  <td>{row.status || "—"}</td>
                  <td>{formatCurrency(row.total_amount)}</td>
                  <td>{formatDateTime(row.created_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Goals Table (shared by Weekly and Daily) ────────────────────────────────
function GoalsTable({
  locations,
  summary,
  goalKey,
  editing,
  saving,
  canEditGoals,
  onStartEdit,
  onSaveGoal,
  onCancelEdit,
  onEditChange,
}) {
  const totalPct =
    summary.total_goal > 0
      ? Math.min(
          100,
          Math.round((summary.total_sold / summary.total_goal) * 100),
        )
      : 0;

  return (
    <table className="data-table">
      <thead>
        <tr>
          <th>Location</th>
          <th>Sold</th>
          <th>Goal</th>
          <th>Progress</th>
        </tr>
      </thead>
      <tbody>
        <tr className="sm-goals-total-row">
          <td>
            <strong>All Locations</strong>
          </td>
          <td>
            <strong>{summary.total_sold}</strong>
          </td>
          <td>
            <strong>{summary.total_goal}</strong>
          </td>
          <td>
            <div className="sm-progress-row">
              <div className="sm-progress-wrap">
                <div
                  className="sm-progress-bar"
                  style={{ width: `${totalPct}%` }}
                />
              </div>
              <span className="sm-progress-pct">{totalPct}%</span>
            </div>
          </td>
        </tr>
        {locations.map((loc) => {
          const goal = loc[goalKey] || 0;
          const pct =
            goal > 0
              ? Math.min(100, Math.round((loc.jobs_sold / goal) * 100))
              : 0;
          const isEditing = editing[loc.location_id] !== undefined;
          return (
            <tr key={loc.location_id}>
              <td>{shortLocationName(loc.location_name)}</td>
              <td>{loc.jobs_sold}</td>
              <td>
                {isEditing ? (
                  <span className="sm-goal-edit-row">
                    <input
                      type="number"
                      min={0}
                      className="sm-goal-input"
                      value={editing[loc.location_id]}
                      onChange={(e) =>
                        onEditChange(loc.location_id, e.target.value)
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") onSaveGoal(loc.location_id);
                        if (e.key === "Escape") onCancelEdit(loc.location_id);
                      }}
                      autoFocus
                    />
                    <button
                      className="sm-goal-save-btn"
                      onClick={() => onSaveGoal(loc.location_id)}
                      disabled={saving[loc.location_id]}
                    >
                      ✓
                    </button>
                    <button
                      className="sm-goal-cancel-btn"
                      onClick={() => onCancelEdit(loc.location_id)}
                    >
                      ✕
                    </button>
                  </span>
                ) : (
                  <span className="sm-goal-display">
                    {goal}
                    {canEditGoals && (
                      <button
                        className="sm-goal-edit-btn"
                        onClick={() => onStartEdit(loc.location_id, goal)}
                        title="Edit goal"
                      >
                        ✎
                      </button>
                    )}
                  </span>
                )}
              </td>
              <td>
                <div className="sm-progress-row">
                  <div className="sm-progress-wrap">
                    <div
                      className={`sm-progress-bar${pct >= 100 ? " complete" : ""}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="sm-progress-pct">{pct}%</span>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ─── Weekly Goals Section ────────────────────────────────────────────────────
function WeeklyGoalsSection({ dashDate }) {
  const { can } = useAuth();
  const canEditGoals = can('dashboard.edit_goals');
  const [activeTab, setActiveTab] = useState("weekly");

  const [weekData, setWeekData] = useState(null);
  const [weekLoading, setWeekLoading] = useState(true);
  const [weekError, setWeekError] = useState(null);
  const [weekEditing, setWeekEditing] = useState({});
  const [weekSaving, setWeekSaving] = useState({});

  const [dayData, setDayData] = useState(null);
  const [dayLoading, setDayLoading] = useState(true);
  const [dayError, setDayError] = useState(null);
  const [dayEditing, setDayEditing] = useState({});
  const [daySaving, setDaySaving] = useState({});

  const fetchWeekly = useCallback(async () => {
    setWeekError(null);
    try {
      const qs = dashDate ? `?date=${dashDate}` : "";
      const res = await fetchWithAuth(`/admin/dashboard/weekly-goals${qs}`);
      const json = await res.json();
      if (res.ok && json.success) setWeekData(json);
      else setWeekError(json.error || "Failed to load weekly goals.");
    } catch {
      setWeekError("Could not connect.");
    } finally {
      setWeekLoading(false);
    }
  }, [dashDate]);

  const fetchDaily = useCallback(async () => {
    setDayError(null);
    try {
      const qs = dashDate ? `?date=${dashDate}` : "";
      const res = await fetchWithAuth(`/admin/dashboard/daily-goals${qs}`);
      const json = await res.json();
      if (res.ok && json.success) setDayData(json);
      else setDayError(json.error || "Failed to load daily goals.");
    } catch {
      setDayError("Could not connect.");
    } finally {
      setDayLoading(false);
    }
  }, [dashDate]);

  useEffect(() => {
    setWeekLoading(true);
    setDayLoading(true);
    fetchWeekly();
    fetchDaily();
  }, [fetchWeekly, fetchDaily]);
  usePoll(() => Promise.all([fetchWeekly(), fetchDaily()]), 120000, { immediate: false });

  const saveWeeklyGoal = async (locationId) => {
    const val = parseInt(weekEditing[locationId]);
    if (isNaN(val) || val < 0) return;
    setWeekSaving((p) => ({ ...p, [locationId]: true }));
    try {
      const res = await fetchWithAuth(
        `/admin/dashboard/weekly-goals/${locationId}`,
        { method: "PUT", body: JSON.stringify({ weeklyGoal: val }) },
      );
      if (res.ok) {
        setWeekEditing((p) => {
          const n = { ...p };
          delete n[locationId];
          return n;
        });
        fetchWeekly();
      }
    } finally {
      setWeekSaving((p) => ({ ...p, [locationId]: false }));
    }
  };

  const saveDailyGoal = async (locationId) => {
    const val = parseInt(dayEditing[locationId]);
    if (isNaN(val) || val < 0) return;
    setDaySaving((p) => ({ ...p, [locationId]: true }));
    try {
      const res = await fetchWithAuth(
        `/admin/dashboard/daily-goals/${locationId}`,
        { method: "PUT", body: JSON.stringify({ dailyGoal: val }) },
      );
      if (res.ok) {
        setDayEditing((p) => {
          const n = { ...p };
          delete n[locationId];
          return n;
        });
        fetchDaily();
      }
    } finally {
      setDaySaving((p) => ({ ...p, [locationId]: false }));
    }
  };

  const loading = activeTab === "weekly" ? weekLoading : dayLoading;
  const error = activeTab === "weekly" ? weekError : dayError;

  if (loading) return <div className="tab-loading">Loading goals…</div>;
  if (error) return <ErrorCard message={error} />;

  return (
    <div className="card">
      <div className="card-header">
        <h3>Jobs Sold</h3>
        <div className="sm-goals-tabs">
          <button
            className={`sm-goals-tab${activeTab === "weekly" ? " active" : ""}`}
            onClick={() => setActiveTab("weekly")}
          >
            Weekly
          </button>
          <button
            className={`sm-goals-tab${activeTab === "daily" ? " active" : ""}`}
            onClick={() => setActiveTab("daily")}
          >
            Daily
          </button>
        </div>
        <span className="sm-badge">
          {activeTab === "weekly" && weekData
            ? `Week of ${formatDate(weekData.weekStart)}`
            : dayData
              ? new Date(dayData.date + "T12:00:00").toLocaleDateString(
                  "en-US",
                  { month: "short", day: "numeric", year: "numeric" },
                )
              : ""}
        </span>
      </div>

      {activeTab === "weekly" && weekData && (
        <GoalsTable
          locations={weekData.locations}
          summary={weekData.summary}
          goalKey="weekly_goal"
          editing={weekEditing}
          saving={weekSaving}
          canEditGoals={canEditGoals}
          onStartEdit={(id, cur) =>
            setWeekEditing((p) => ({ ...p, [id]: String(cur) }))
          }
          onSaveGoal={saveWeeklyGoal}
          onCancelEdit={(id) =>
            setWeekEditing((p) => {
              const n = { ...p };
              delete n[id];
              return n;
            })
          }
          onEditChange={(id, val) =>
            setWeekEditing((p) => ({ ...p, [id]: val }))
          }
        />
      )}

      {activeTab === "daily" && dayData && (
        <GoalsTable
          locations={dayData.locations}
          summary={dayData.summary}
          goalKey="daily_goal"
          editing={dayEditing}
          saving={daySaving}
          canEditGoals={canEditGoals}
          onStartEdit={(id, cur) =>
            setDayEditing((p) => ({ ...p, [id]: String(cur) }))
          }
          onSaveGoal={saveDailyGoal}
          onCancelEdit={(id) =>
            setDayEditing((p) => {
              const n = { ...p };
              delete n[id];
              return n;
            })
          }
          onEditChange={(id, val) =>
            setDayEditing((p) => ({ ...p, [id]: val }))
          }
        />
      )}
    </div>
  );
}

// ─── Main Dashboard Page ─────────────────────────────────────────────────────
function DashboardPage() {
  const { effectiveRole } = useAuth();

  const visibleTabs = TABS.filter((t) => {
    if (!t.roles) return true;
    return t.roles.includes(effectiveRole);
  });

  const [activeTab, setActiveTab] = useState("dashboard");

  // Dashboard date picker
  const todayEST = new Date().toLocaleDateString("en-CA", {
    timeZone: "America/New_York",
  });
  const [dashDate, setDashDate] = useState(todayEST);
  const isDashToday = dashDate === todayEST;

  const [tabData, setTabData] = useState({});
  const [tabLoading, setTabLoading] = useState({});
  const [tabError, setTabError] = useState({});

  // Refresh display
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshDisplay, setRefreshDisplay] = useState("");

  useEffect(() => {
    if (!lastRefreshed) return;
    const update = () => {
      const secs = Math.floor((Date.now() - lastRefreshed.getTime()) / 1000);
      if (secs < 10) setRefreshDisplay("Just now");
      else if (secs < 60)
        setRefreshDisplay(`${Math.floor(secs / 10) * 10}s ago`);
      else setRefreshDisplay(`${Math.floor(secs / 60)}m ago`);
    };
    update();
    const id = setInterval(update, 10000);
    return () => clearInterval(id);
  }, [lastRefreshed]);

  const fetchTab = useCallback(async (tabId, dateOverride) => {
    setTabLoading((prev) => ({ ...prev, [tabId]: true }));
    setTabError((prev) => ({ ...prev, [tabId]: null }));
    try {
      const qs =
        tabId === "dashboard" && dateOverride ? `?date=${dateOverride}` : "";
      const res = await fetchWithAuth(`/admin/dashboard/${tabId === "dashboard" ? "" : tabId}${qs}`);
      const json = await res.json();
      if (!res.ok || !json.success) {
        setTabError((prev) => ({
          ...prev,
          [tabId]: json.error || "Unexpected error.",
        }));
      } else {
        setTabData((prev) => ({ ...prev, [tabId]: json }));
        setLastRefreshed(new Date());
      }
    } catch {
      setTabError((prev) => ({
        ...prev,
        [tabId]: "Could not connect to the server.",
      }));
    } finally {
      setTabLoading((prev) => ({ ...prev, [tabId]: false }));
    }
  }, []);

  // Fetch dashboard data on mount and when date changes
  useEffect(() => {
    if (activeTab === "dashboard") {
      fetchTab("dashboard", dashDate);
    }
  }, [activeTab, dashDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh every 120 seconds
  usePoll(() => fetchTab("dashboard", dashDate), 120000, { enabled: activeTab === "dashboard", immediate: false });

  const handleManualRefresh = async () => {
    setRefreshing(true);
    await fetchTab("dashboard", dashDate);
    setRefreshing(false);
  };

  return (
    <div className="sm-live-page">
      {/* Tab navigation */}
      <nav className="pp-nav">
        {visibleTabs.map((tab) => (
          <button
            key={tab.id}
            className={`pp-nav-item${activeTab === tab.id ? " active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="pp-nav-label">{tab.label}</span>
          </button>
        ))}

        <div className="pp-nav-refresh">
          {lastRefreshed && (
            <span className="pp-nav-refresh-time">
              Updated {refreshDisplay}
            </span>
          )}
          <button
            className={`pp-nav-refresh-btn${refreshing ? " spinning" : ""}`}
            onClick={handleManualRefresh}
            disabled={refreshing}
            title="Refresh data"
          >
            ↻
          </button>
        </div>
      </nav>

      {/* Page heading */}
      <div className="page-header">
        <div>
          <h1>Dashboard</h1>
          <p className="subtitle">
            {visibleTabs.find((t) => t.id === activeTab)?.subtitle}
          </p>
        </div>
        {activeTab === "dashboard" && (
          <div className="sm-dash-date-picker">
            <input
              type="date"
              value={dashDate}
              max={todayEST}
              onChange={(e) => setDashDate(e.target.value)}
              className="sm-dash-date-input"
            />
            <button
              className={`sm-dash-today-btn${isDashToday ? "" : " active"}`}
              disabled={isDashToday}
              onClick={() => setDashDate(todayEST)}
            >
              Today
            </button>
          </div>
        )}
      </div>

      {/* Tab content */}
      {activeTab === "dashboard" && (
        <DashboardTab
          data={tabData.dashboard}
          loading={tabLoading.dashboard}
          error={tabError.dashboard}
          dashDate={dashDate}
        />
      )}
      {activeTab !== "dashboard" && (
        <ComingSoonTab
          label={visibleTabs.find((t) => t.id === activeTab)?.label || activeTab}
        />
      )}
    </div>
  );
}

export default DashboardPage;
